import { styled } from '@linaria/react';
import { Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting';
import { coerceToDateInput } from '../../../helpers/date-time-helpers/parsing/parsing';
import { newCOLORS } from '../../../styles/colors';
import { skyLight } from '../../../styles/design-tokens';
import type { SprintData } from '../hooks/dashboards.hooks.type';
import { buildBurnChartData } from './burn-chart.helpers';

interface BurnChartProps {
  data: SprintData | undefined;
  chartType: 'burn_up' | 'burn_down';
  selectedMetric: 'tasks' | 'points';
}

export const BurnChart = ({ data, chartType, selectedMetric }: BurnChartProps) => {
  // The chart lives inside a react-grid-layout cell that finalizes its size
  // after the first paint. Recharts' ResponsiveContainer can latch onto the
  // pre-layout dimensions on first mount, causing the ideal-pace/ideal-remaining
  // line to render with stale path data until a refresh. Flipping this state
  // post-mount produces a single clean remount once the layout has settled.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const transformedData = useMemo(
    () => buildBurnChartData(data, chartType, selectedMetric),
    [data, chartType, selectedMetric],
  );

  if (!data || transformedData.length === 0) {
    return (
      <EmptyChartMessage>
        <Text>No burn data available for the selected sprint.</Text>
      </EmptyChartMessage>
    );
  }

  return (
    <ChartWrapper>
      <ChartContainer>
        <ResponsiveContainer key={hasMounted ? 'ready' : 'init'} width="100%" height="100%">
          <ComposedChart data={transformedData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value, { format: 'MMM D' })}
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              label={{
                value:
                  chartType === 'burn_down'
                    ? selectedMetric === 'tasks'
                      ? 'Tasks Remaining'
                      : 'Points Remaining'
                    : selectedMetric === 'tasks'
                      ? 'Tasks Completed'
                      : 'Points Completed',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 11 },
                offset: 10,
              }}
            />
            <Tooltip
              formatter={(value, name) => {
                const unit = selectedMetric === 'tasks' ? 'tasks' : 'points';
                // Ideal lines are computed as linear interpolations and yield
                // fractional values; round to whole numbers for tooltip display.
                const displayValue = typeof value === 'number' ? Math.round(value) : value;
                return [`${displayValue} ${unit}`, name];
              }}
              labelFormatter={(label) => `Date: ${formatDate(coerceToDateInput(label), { format: 'MMM D, YYYY' })}`}
            />
            <Legend />

            {chartType === 'burn_down' ? (
              <>
                {/* Total Scope Line */}
                <Line
                  type="monotone"
                  dataKey="totalScope"
                  name="Total Scope"
                  stroke={newCOLORS.purple}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {/* Actual Remaining Line */}
                <Line
                  type="monotone"
                  dataKey="actualRemaining"
                  name="Actual Remaining"
                  stroke={newCOLORS.red}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {/* Ideal Remaining Line */}
                <Line
                  type="monotone"
                  dataKey="idealRemaining"
                  name="Ideal Remaining"
                  stroke={newCOLORS.blue}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </>
            ) : (
              <>
                {/* Total Scope Line for Burn Up */}
                <Line
                  type="monotone"
                  dataKey="totalScope"
                  name="Total Scope"
                  stroke={newCOLORS.purple}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {/* Actual Completed Line for Burn Up */}
                <Line
                  type="monotone"
                  dataKey="actualCompleted"
                  name="Actual Completed"
                  stroke={newCOLORS.green}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                {/* Ideal Completed Line for Burn Up */}
                <Line
                  type="monotone"
                  dataKey="idealCompleted"
                  name="Ideal Pace"
                  stroke={newCOLORS.blue}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
`;

const EmptyChartMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 300px;
  background-color: ${newCOLORS.lightGray};
  border-radius: 4px;
  padding: 16px;
  text-align: center;
`;
